import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, forkJoin, distinctUntilChanged } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { DozentService, CourseParticipant } from '../../../services/dozent.service';
import { DozentSocketService } from '../../../services/dozent-socket.service';
import { DozentNotificationService } from '../../../services/dozent-notification.service';

interface Learner {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  is_online?: boolean;
  last_conversation_at?: string;
  message_notification_count?: number;
  exercise_notification_count?: number;
}

interface Conversation {
  id: string;
  title: string;
  status: string;
  source: string;
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
  course_id?: string | null;
  course_name?: string | null;
  last_message_at?: string;
  unread_count?: number;
}

interface ConversationMessage {
  id: string;
  conversation_id: string;
  sender_type: string;
  sender_user_id: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

type LearnerSortMode = 'priority' | 'name_asc' | 'name_desc' | 'last_active_desc' | 'last_active_asc';
type ConversationSortMode = 'recent' | 'oldest' | 'title_asc' | 'title_desc';

@Component({
  selector: 'app-dozent-messages',
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.scss',
})
export class DozentMessagesComponent implements OnInit, OnDestroy {
  @ViewChild('messageContainer') messageContainer!: ElementRef;

  // Left panel
  learners: Learner[] = [];
  filteredLearners: Learner[] = [];
  selectedLearner: Learner | null = null;
  searchText = '';
  isLoadingLearners = true;
  learnerSortMode: LearnerSortMode = 'priority';
  learnerPage = 0;
  learnerPageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];

  // Right panel
  conversations: Conversation[] = [];
  selectedConversation: Conversation | null = null;
  messages: ConversationMessage[] = [];
  isLoadingConversations = false;
  isLoadingMessages = false;
  activeTab = 'conversations';
  conversationSortMode: ConversationSortMode = 'recent';
  conversationPage = 0;
  conversationPageSize = 25;

  // Chat
  newMessageText = '';
  isSending = false;

  // New conversation
  showNewConversationForm = false;
  newConversationTitle = '';
  newConversationMessage = '';
  isCreating = false;

  // Exercises
  exercises: any[] = [];
  isLoadingExercises = false;
  expandedExercise: string | null = null;
  exercisePage = 0;
  exercisePageSize = 25;

  private subs: Subscription[] = [];
  private exerciseNotificationCounts = new Map<string, number>();

  constructor(
    private dozentService: DozentService,
    private socketService: DozentSocketService,
    private notificationService: DozentNotificationService,
    private toastr: NbToastrService,
    private route: ActivatedRoute,
    protected t: TranslateService
  ) {}

  ngOnInit() {
    this.socketService.connect();
    this.loadLearners(() => this.handleDeepLink());

    // Re-handle deep link on queryParam changes (if user clicks another
    // notification while already on this page)
    this.subs.push(
      this.route.queryParamMap.subscribe(() => this.handleDeepLink()),
    );

    // Listen for new messages via WebSocket. Updates two surfaces:
    //   1. The currently selected thread — append the message + scroll.
    //   2. The conversations list — bump preview + unread + move-to-top
    //      so the dozent doesn't need to refresh to see "new message
    //      arrived in a different thread".
    this.subs.push(
      this.socketService.newMessage$.subscribe((msg) => {
        if (!msg?.conversation_id) return;

        const idx = this.conversations.findIndex(
          (c) => c.id === msg.conversation_id
        );
        const isActive =
          !!this.selectedConversation &&
          this.selectedConversation.id === msg.conversation_id;
        const learnerId =
          msg.learner_user_id ||
          this.findLearnerIdForConversation(msg.conversation_id) ||
          (idx !== -1 ? this.selectedLearner?.user_id : undefined);

        // (1) Same-thread append.
        if (isActive) {
          this.messages.push(msg);
          this.socketService.markRead(msg.conversation_id);
          setTimeout(() => this.scrollToBottom(), 50);
        }

        // (2) Conversation-list patch.
        if (idx !== -1) {
          const updated = {
            ...this.conversations[idx],
            last_message_preview: msg.message ?? this.conversations[idx].last_message_preview,
            last_message_at: new Date().toISOString(),
            // Don't increment unread for the actively-open thread —
            // the dozent already sees it.
            unread_count: isActive
              ? this.conversations[idx].unread_count
              : (this.conversations[idx].unread_count ?? 0) + 1,
          };
          this.conversations = [
            updated,
            ...this.conversations.slice(0, idx),
            ...this.conversations.slice(idx + 1),
          ];
          if (isActive) {
            this.markConversationReadLocally(msg.conversation_id);
          } else if (this.selectedLearner) {
            this.syncLearnerMessageMarkerFromConversations(
              this.selectedLearner.user_id
            );
          }
        } else if (!isActive && learnerId) {
          this.bumpLearnerMarker(learnerId, 'message');
        }
      })
    );

    // Listen for presence updates
    this.subs.push(
      this.socketService.presenceUpdate$.subscribe((update) => {
        const learner = this.learners.find(
          (l) => l.user_id === update.user_id
        );
        if (learner) {
          learner.is_online = update.is_online;
        }
      })
    );

    // Generic notification stream. We route per `type`:
    //   • `ai_escalation` / `new_message` → re-load the conversations
    //     list for the active learner so the new conversation card
    //     appears without a refresh. Cheap re-fetch keeps shape
    //     canonical instead of mutating from a partial payload.
    //   • other types (e.g. `submission_review`, `learning_suggestion`)
    //     are handled by their respective pages; the bell badge is
    //     bumped centrally by `DozentNotificationService`.
    this.subs.push(
      this.socketService.notification$.subscribe((notif) => {
        if (!notif) return;
        const meta = notif.metadata || {};
        const learnerId = meta.learner_user_id;

        if (notif.type === 'submission_review') {
          if (learnerId) {
            this.bumpLearnerMarker(learnerId, 'exercise');
          }
          this.loadLearnerNotificationMarkers();
          if (
            learnerId &&
            this.selectedLearner?.user_id === learnerId &&
            this.activeTab === 'exercises'
          ) {
            this.loadExercises();
          }
          return;
        }

        if (notif.type !== 'ai_escalation' && notif.type !== 'new_message') return;
        if (notif.type === 'ai_escalation' && learnerId) {
          this.bumpLearnerMarker(learnerId, 'message');
        }
        const conversationId = meta.conversation_id || notif.reference_id;
        const notificationBelongsToSelected =
          !learnerId || learnerId === this.selectedLearner?.user_id;
        const activeConversationAlreadyOpen =
          conversationId && this.selectedConversation?.id === conversationId;
        if (
          this.selectedLearner &&
          notificationBelongsToSelected &&
          !activeConversationAlreadyOpen
        ) {
          this.loadConversationsForLearner(this.selectedLearner.user_id);
        }
      })
    );

    this.subs.push(
      this.notificationService.unreadCount$
        .pipe(distinctUntilChanged())
        .subscribe(() => this.loadLearnerNotificationMarkers())
    );
  }

  onDozentFilterChange() {
    this.selectedLearner = null;
    this.selectedConversation = null;
    this.conversations = [];
    this.messages = [];
    this.exerciseNotificationCounts.clear();
    this.loadLearners();
  }

  loadLearners(onDone?: () => void) {
    this.isLoadingLearners = true;

    // Load courses first, then participants from all courses + conversations
    this.dozentService.listCourses().subscribe({
      next: (courses: any[]) => {
        const participantRequests = courses.map((c: any) =>
          this.dozentService.listParticipants(c.id)
        );

        // Also load conversations for last_conversation_at info
        const sources$ = participantRequests.length > 0
          ? forkJoin([...participantRequests, this.dozentService.listConversations()])
          : forkJoin([this.dozentService.listConversations()]);

        sources$.subscribe({
          next: (results: any[]) => {
            const learnerMap = new Map<string, Learner>();

            // Last result is conversations, rest are participant arrays
            const convResult = results[results.length - 1] as any[];
            const participantResults = results.slice(0, -1) as CourseParticipant[][];

            // Add all course participants
            for (const participants of participantResults) {
              for (const p of participants) {
                const uid = p.learner_user_id;
                if (!uid || learnerMap.has(uid)) continue;
                learnerMap.set(uid, {
                  user_id: uid,
                  first_name: p.first_name || '',
                  last_name: p.last_name || '',
                  email: p.email || '',
                  message_notification_count: 0,
                  exercise_notification_count:
                    this.exerciseNotificationCounts.get(uid) ?? 0,
                });
              }
            }

            // Merge conversation data (names, last_conversation_at)
            for (const conv of convResult) {
              const uid = conv.learner_user_id;
              if (!uid) continue;
              if (learnerMap.has(uid)) {
                const existing = learnerMap.get(uid)!;
                if (!existing.first_name && conv.learner_first_name) {
                  existing.first_name = conv.learner_first_name;
                }
                if (!existing.last_name && conv.learner_last_name) {
                  existing.last_name = conv.learner_last_name;
                }
                if (
                  !existing.last_conversation_at ||
                  Date.parse(conv.updated_at) > Date.parse(existing.last_conversation_at)
                ) {
                  existing.last_conversation_at = conv.updated_at;
                }
                existing.message_notification_count =
                  (existing.message_notification_count ?? 0) +
                  this.toCount(conv.unread_count);
              } else {
                learnerMap.set(uid, {
                  user_id: uid,
                  first_name: conv.learner_first_name || '',
                  last_name: conv.learner_last_name || '',
                  email: conv.learner_email || '',
                  last_conversation_at: conv.updated_at,
                  message_notification_count: this.toCount(conv.unread_count),
                  exercise_notification_count:
                    this.exerciseNotificationCounts.get(uid) ?? 0,
                });
              }
            }

            this.learners = Array.from(learnerMap.values());
            this.applyLearnerNotificationMarkers();
            this.loadLearnerNotificationMarkers();
            this.filterLearners();
            this.clampLearnerPage();
            this.fetchOnlineStatuses();
            this.isLoadingLearners = false;
            onDone?.();
          },
          error: () => {
            this.isLoadingLearners = false;
            onDone?.();
          },
        });
      },
      error: () => {
        this.isLoadingLearners = false;
      },
    });
  }

  fetchOnlineStatuses() {
    const ids = this.learners.map((l) => l.user_id);
    if (ids.length === 0) return;
    this.dozentService.getOnlineStatuses(ids).subscribe({
      next: (statuses: any) => {
        for (const learner of this.learners) {
          learner.is_online = statuses[learner.user_id] || false;
        }
      },
    });
  }

  filterLearners() {
    if (!this.searchText) {
      this.filteredLearners = [...this.learners];
    } else {
      const q = this.searchText.toLowerCase();
      this.filteredLearners = this.learners.filter(
        (l) =>
          l.first_name.toLowerCase().includes(q) ||
          l.last_name.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q)
      );
    }
    this.filteredLearners.sort((a, b) => {
      if (this.learnerSortMode === 'name_asc' || this.learnerSortMode === 'name_desc') {
        const cmp = this.getLearnerName(a).localeCompare(
          this.getLearnerName(b),
          undefined,
          { sensitivity: 'base', numeric: true }
        );
        return this.learnerSortMode === 'name_asc' ? cmp : -cmp;
      }
      if (
        this.learnerSortMode === 'last_active_desc' ||
        this.learnerSortMode === 'last_active_asc'
      ) {
        const cmp = this.getLearnerLastActiveTime(b) - this.getLearnerLastActiveTime(a);
        return this.learnerSortMode === 'last_active_desc' ? cmp : -cmp;
      }

      // Priority sort: learners with pending message/exercise signals first,
      // then online, then most recent conversation, then alphabetical.
      const aMarked = this.hasAnyNotificationMarker(a);
      const bMarked = this.hasAnyNotificationMarker(b);
      if (aMarked && !bMarked) return -1;
      if (!aMarked && bMarked) return 1;
      if (a.is_online && !b.is_online) return -1;
      if (!a.is_online && b.is_online) return 1;
      const activityCmp = this.getLearnerLastActiveTime(b) - this.getLearnerLastActiveTime(a);
      if (activityCmp !== 0) return activityCmp;
      return this.getLearnerName(a).localeCompare(this.getLearnerName(b), undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    });
    this.clampLearnerPage();
  }

  get pagedLearners(): Learner[] {
    const maxPage = Math.max(Math.ceil(this.filteredLearners.length / this.learnerPageSize) - 1, 0);
    const page = Math.min(this.learnerPage, maxPage);
    const start = page * this.learnerPageSize;
    return this.filteredLearners.slice(start, start + this.learnerPageSize);
  }

  onLearnerSearchChange(): void {
    this.learnerPage = 0;
    this.filterLearners();
  }

  onLearnerSortChange(mode: LearnerSortMode): void {
    this.learnerSortMode = mode;
    this.learnerPage = 0;
    this.filterLearners();
  }

  onLearnerPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.filteredLearners.length / this.learnerPageSize) - 1, 0);
    this.learnerPage = Math.max(0, Math.min(page, maxPage));
  }

  onLearnerPageSizeChange(size: number): void {
    this.learnerPageSize = Math.min(Math.max(size, 1), 100);
    this.learnerPage = 0;
  }

  onSelectLearner(learner: Learner) {
    this.selectedLearner = learner;
    this.selectedConversation = null;
    this.messages = [];
    this.activeTab = 'conversations';
    this.conversationPage = 0;
    this.exercisePage = 0;
    this.loadConversationsForLearner(learner.user_id);
  }

  loadConversationsForLearner(learnerId: string, selectConversationId?: string) {
    this.isLoadingConversations = true;
    this.dozentService.getParticipantConversations(learnerId).subscribe({
      next: (convs: any[]) => {
        this.conversations = convs;
        this.syncLearnerMessageMarkerFromConversations(learnerId, convs);
        this.isLoadingConversations = false;
        if (selectConversationId) {
          const target = convs.find((c) => c.id === selectConversationId);
          if (target) {
            this.ensureConversationVisible(selectConversationId);
            this.onSelectConversation(target);
          }
        }
      },
      error: () => {
        this.isLoadingConversations = false;
      },
    });
  }

  private handleDeepLink() {
    const conversationId = this.route.snapshot.queryParamMap.get('conversationId');
    if (!conversationId || this.isLoadingLearners) return;

    // If this conversation is already selected, nothing to do.
    if (this.selectedConversation?.id === conversationId) return;

    // Look up the learner_user_id for this conversation via the global list.
    this.dozentService.listConversations().subscribe({
      next: (all: any[]) => {
        const match = all.find((c) => c.id === conversationId);
        if (!match) return;
        const learner = this.learners.find(
          (l) => l.user_id === match.learner_user_id,
        );
        if (!learner) return;
        this.selectedLearner = learner;
        this.filterLearners();
        this.loadConversationsForLearner(learner.user_id, conversationId);
      },
    });
  }

  onSelectConversation(conv: Conversation) {
    if (this.selectedConversation?.id === conv.id) {
      this.selectedConversation = null;
      this.messages = [];
      return;
    }
    this.selectedConversation = conv;
    this.loadMessages(conv.id);
  }

  loadMessages(conversationId: string) {
    this.isLoadingMessages = true;
    this.dozentService.getConversationMessages(conversationId).subscribe({
      next: (msgs: any[]) => {
        this.messages = msgs;
        this.isLoadingMessages = false;
        setTimeout(() => this.scrollToBottom(), 50);
        // Mark messages as read
        this.socketService.markRead(conversationId);
        this.markConversationReadLocally(conversationId);
      },
      error: () => {
        this.isLoadingMessages = false;
      },
    });
  }

  onSendMessage() {
    if (!this.newMessageText.trim() || !this.selectedConversation) return;
    this.isSending = true;
    this.dozentService
      .sendConversationMessage(
        this.selectedConversation.id,
        this.newMessageText.trim()
      )
      .subscribe({
        next: (msg: any) => {
          this.messages.push(msg);
          this.newMessageText = '';
          this.isSending = false;
          setTimeout(() => this.scrollToBottom(), 50);
        },
        error: () => {
          this.isSending = false;
          this.toastr.danger(
            this.t.instant('pages.dozent.messages.sendError'),
            'Error'
          );
        },
      });
  }

  onCreateConversation() {
    if (!this.newConversationMessage.trim() || !this.selectedLearner) return;
    this.isCreating = true;
    this.dozentService
      .createConversation({
        learner_user_id: this.selectedLearner.user_id,
        title: this.newConversationTitle || undefined,
        initial_message: this.newConversationMessage.trim(),
      })
      .subscribe({
        next: (conv: any) => {
          this.conversations.unshift(conv);
          this.showNewConversationForm = false;
          this.newConversationTitle = '';
          this.newConversationMessage = '';
          this.isCreating = false;
          this.conversationPage = 0;
          this.onSelectConversation(conv);
        },
        error: () => {
          this.isCreating = false;
          this.toastr.danger(
            this.t.instant('pages.dozent.messages.createError'),
            'Error'
          );
        },
      });
  }

  onResolveConversation() {
    if (!this.selectedConversation) return;
    this.dozentService
      .resolveConversation(this.selectedConversation.id)
      .subscribe({
        next: () => {
          if (this.selectedConversation) {
            this.selectedConversation.status = 'resolved';
          }
          const conv = this.conversations.find(
            (c) => c.id === this.selectedConversation?.id
          );
          if (conv) conv.status = 'resolved';
          this.toastr.success(
            this.t.instant('pages.dozent.messages.resolved'),
            'OK'
          );
        },
      });
  }

  onTabChange(tab: string) {
    this.activeTab = tab;
    if (tab === 'exercises' && this.selectedLearner && this.exercises.length === 0) {
      this.loadExercises();
    }
  }

  loadExercises() {
    if (!this.selectedLearner) return;
    this.isLoadingExercises = true;
    this.dozentService
      .getParticipantExercises(this.selectedLearner.user_id)
      .subscribe({
        next: (ex: any[]) => {
          this.exercises = ex;
          this.clampExercisePage();
          this.isLoadingExercises = false;
        },
        error: () => {
          this.isLoadingExercises = false;
        },
      });
  }

  get pagedExercises(): any[] {
    const maxPage = Math.max(Math.ceil(this.exercises.length / this.exercisePageSize) - 1, 0);
    const page = Math.min(this.exercisePage, maxPage);
    const start = page * this.exercisePageSize;
    return this.exercises.slice(start, start + this.exercisePageSize);
  }

  onExercisePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.exercises.length / this.exercisePageSize) - 1, 0);
    this.exercisePage = Math.max(0, Math.min(page, maxPage));
  }

  onExercisePageSizeChange(size: number): void {
    this.exercisePageSize = Math.min(Math.max(size, 1), 100);
    this.exercisePage = 0;
  }

  toggleExercise(id: string) {
    this.expandedExercise = this.expandedExercise === id ? null : id;
  }

  hasMessageMarker(learner: Learner): boolean {
    return this.toCount(learner.message_notification_count) > 0;
  }

  hasExerciseMarker(learner: Learner): boolean {
    return this.toCount(learner.exercise_notification_count) > 0;
  }

  hasAnyNotificationMarker(learner: Learner): boolean {
    return this.hasMessageMarker(learner) || this.hasExerciseMarker(learner);
  }

  getLearnerName(l: Learner): string {
    if (l.first_name || l.last_name) {
      return `${l.first_name} ${l.last_name}`.trim();
    }
    return l.email || this.t.instant('pages.dozent.messages.learnerFallback');
  }

  getInitials(l: Learner): string {
    const parts = [l.first_name, l.last_name]
      .map((p) => (p || '').trim())
      .filter((p) => p.length > 0);
    if (parts.length > 0) {
      return parts
        .slice(0, 2)
        .map((p) => p[0]!.toUpperCase())
        .join('');
    }
    return (l.email || '?').charAt(0).toUpperCase();
  }

  getSenderLabel(msg: ConversationMessage): string {
    if (msg.sender_type === 'dozent')
      return this.t.instant('pages.dozent.messages.you');
    if (msg.sender_type === 'system') return 'System';
    if (msg.sender_type === 'ai') return 'AI';
    return this.selectedLearner
      ? this.getLearnerName(this.selectedLearner)
      : this.t.instant('pages.dozent.messages.learnerFallback');
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'open':
        return 'open';
      case 'resolved':
        return 'resolved';
      case 'archived':
        return 'archived';
      default:
        return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'open':
        return this.t.instant('pages.dozent.messages.statusOpen');
      case 'resolved':
        return this.t.instant('pages.dozent.messages.statusResolved');
      case 'archived':
        return this.t.instant('pages.dozent.messages.statusArchived');
      default:
        return status;
    }
  }

  getSourceLabel(source: string): string {
    switch (source) {
      case 'learner':
        return this.t.instant('pages.dozent.messages.sourceLearner');
      case 'dozent':
        return this.t.instant('pages.dozent.messages.sourceDozent');
      case 'ai_escalation':
        return this.t.instant('pages.dozent.messages.sourceAi');
      default:
        return source;
    }
  }

  isOwnMessage(msg: ConversationMessage): boolean {
    return msg.sender_type === 'dozent';
  }

  get sortedConversations(): Conversation[] {
    return [...this.conversations].sort((a, b) => {
      if (this.conversationSortMode === 'title_asc' || this.conversationSortMode === 'title_desc') {
        const cmp = (a.title || '').localeCompare((b.title || ''), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        return this.conversationSortMode === 'title_asc' ? cmp : -cmp;
      }
      const cmp = this.getConversationTime(b) - this.getConversationTime(a);
      return this.conversationSortMode === 'recent' ? cmp : -cmp;
    });
  }

  get pagedConversations(): Conversation[] {
    const rows = this.sortedConversations;
    const maxPage = Math.max(Math.ceil(rows.length / this.conversationPageSize) - 1, 0);
    const page = Math.min(this.conversationPage, maxPage);
    const start = page * this.conversationPageSize;
    return rows.slice(start, start + this.conversationPageSize);
  }

  onConversationSortChange(mode: ConversationSortMode): void {
    this.conversationSortMode = mode;
    this.conversationPage = 0;
  }

  onConversationPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.conversations.length / this.conversationPageSize) - 1, 0);
    this.conversationPage = Math.max(0, Math.min(page, maxPage));
  }

  onConversationPageSizeChange(size: number): void {
    this.conversationPageSize = Math.min(Math.max(size, 1), 100);
    this.conversationPage = 0;
  }

  private scrollToBottom() {
    try {
      if (this.messageContainer) {
        this.messageContainer.nativeElement.scrollTop =
          this.messageContainer.nativeElement.scrollHeight;
      }
    } catch {}
  }

  private loadLearnerNotificationMarkers() {
    this.dozentService.listNotificationLearnerMarkers().subscribe({
      next: (markers) => {
        this.exerciseNotificationCounts = new Map(
          markers.map((m) => [m.learner_user_id, this.toCount(m.exercises)])
        );
        this.applyLearnerNotificationMarkers();
      },
    });
  }

  private applyLearnerNotificationMarkers() {
    this.learners = this.learners.map((learner) => ({
      ...learner,
      exercise_notification_count:
        this.exerciseNotificationCounts.get(learner.user_id) ?? 0,
    }));
    if (this.selectedLearner) {
      const selected = this.learners.find(
        (learner) => learner.user_id === this.selectedLearner?.user_id
      );
      if (selected) {
        this.selectedLearner = selected;
      }
    }
    this.filterLearners();
  }

  private syncLearnerMessageMarkerFromConversations(
    learnerId: string,
    conversations = this.conversations
  ) {
    const count = conversations.reduce(
      (sum, conv) => sum + this.toCount(conv.unread_count),
      0
    );
    this.setLearnerMarkerCount(learnerId, 'message', count);
  }

  private markConversationReadLocally(conversationId: string) {
    this.conversations = this.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
    );
    if (this.selectedConversation?.id === conversationId) {
      this.selectedConversation = {
        ...this.selectedConversation,
        unread_count: 0,
      };
    }
    if (this.selectedLearner) {
      this.syncLearnerMessageMarkerFromConversations(
        this.selectedLearner.user_id
      );
    }
  }

  private findLearnerIdForConversation(conversationId: string): string | undefined {
    if (
      this.selectedLearner &&
      this.conversations.some((conv) => conv.id === conversationId)
    ) {
      return this.selectedLearner.user_id;
    }
    return undefined;
  }

  private bumpLearnerMarker(learnerId: string, kind: 'message' | 'exercise') {
    const learner = this.learners.find((item) => item.user_id === learnerId);
    if (!learner) {
      this.loadLearners();
      return;
    }
    const key: 'message_notification_count' | 'exercise_notification_count' =
      kind === 'message'
        ? 'message_notification_count'
        : 'exercise_notification_count';
    this.setLearnerMarkerCount(learnerId, kind, this.toCount(learner[key]) + 1);
  }

  private setLearnerMarkerCount(
    learnerId: string,
    kind: 'message' | 'exercise',
    count: number
  ) {
    const key: 'message_notification_count' | 'exercise_notification_count' =
      kind === 'message'
        ? 'message_notification_count'
        : 'exercise_notification_count';
    this.learners = this.learners.map((learner) =>
      learner.user_id === learnerId ? { ...learner, [key]: count } : learner
    );
    if (this.selectedLearner?.user_id === learnerId) {
      this.selectedLearner = { ...this.selectedLearner, [key]: count };
    }
    this.filterLearners();
  }

  private toCount(value: unknown): number {
    const count = Number(value ?? 0);
    return Number.isFinite(count) ? count : 0;
  }

  private clampLearnerPage(): void {
    const maxPage = Math.max(Math.ceil(this.filteredLearners.length / this.learnerPageSize) - 1, 0);
    this.learnerPage = Math.max(0, Math.min(this.learnerPage, maxPage));
  }

  private getLearnerLastActiveTime(learner: Learner): number {
    const time = Date.parse(learner.last_conversation_at || '');
    return Number.isFinite(time) ? time : 0;
  }

  private getConversationTime(conv: Conversation): number {
    const time = Date.parse(conv.last_message_at || conv.updated_at || conv.created_at || '');
    return Number.isFinite(time) ? time : 0;
  }

  private ensureConversationVisible(conversationId: string): void {
    const index = this.sortedConversations.findIndex((conv) => conv.id === conversationId);
    if (index >= 0) {
      this.conversationPage = Math.floor(index / this.conversationPageSize);
    }
  }

  private clampExercisePage(): void {
    const maxPage = Math.max(Math.ceil(this.exercises.length / this.exercisePageSize) - 1, 0);
    this.exercisePage = Math.max(0, Math.min(this.exercisePage, maxPage));
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }
}
